/**
 * Nosto-Client.js
 *
 * Centraliza la comunicación con la API JavaScript de Nosto.
 * Ejecuta búsquedas, normaliza respuestas y expone métodos compartidos
 * de tracking para los controllers de PLP y Search.
 *
 * Este archivo no contiene lógica específica de UI, filtros, paginación,
 * URL, product cards ni comportamiento particular de un controller.
 */

(function initializeNostoClient(global) {
    "use strict";

    class NostoClient {
        /**
         * Crea una instancia del cliente de Nosto con opciones configurables.
         */
        constructor({
            apiProvider = null,
            apiTimeout = 10000,
            debug = false,
        } = {}) {
            this.apiProvider =
                apiProvider ||
                ((callback) => {
                    global.nostojs(callback);
                });

            this.apiTimeout = apiTimeout;
            this.debug = debug;
        }

        /**
         * Escribe mensajes informativos únicamente cuando el modo debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info("[NostoClient]", ...args);
        }

        /**
         * Escribe advertencias identificadas como mensajes de NostoClient.
         */
        warn(...args) {
            console.warn("[NostoClient]", ...args);
        }

        /**
         * Comprueba que la función global nostojs esté disponible.
         */
        isAvailable() {
            return typeof global.nostojs === "function";
        }

        /**
         * Obtiene una instancia de la API de Nosto y la devuelve mediante una Promise.
         */
        getApi() {
            return new Promise((resolve, reject) => {
                if (!this.isAvailable() && !this.apiProvider) {
                    reject(
                        new Error(
                            "Nosto no está disponible. No se encontró la función global nostojs."
                        )
                    );
                    return;
                }

                let settled = false;

                const timeoutId = global.setTimeout(() => {
                    if (settled) return;

                    settled = true;

                    reject(
                        new Error(
                            `Nosto no respondió dentro de ${this.apiTimeout} ms.`
                        )
                    );
                }, this.apiTimeout);

                try {
                    this.apiProvider((api) => {
                        if (settled) return;

                        if (!api) {
                            settled = true;
                            global.clearTimeout(timeoutId);

                            reject(
                                new Error("Nosto devolvió una instancia de API inválida.")
                            );

                            return;
                        }

                        settled = true;
                        global.clearTimeout(timeoutId);
                        resolve(api);
                    });
                } catch (error) {
                    if (settled) return;

                    settled = true;
                    global.clearTimeout(timeoutId);
                    reject(error);
                }
            });
        }

        /**
         * Ejecuta una operación personalizada recibiendo directamente la API de Nosto.
         */
        async execute(callback) {
            if (typeof callback !== "function") {
                throw new TypeError(
                    "NostoClient.execute requiere una función callback."
                );
            }

            const api = await this.getApi();

            return callback(api);
        }

        /**
         * Convierte un valor en un arreglo seguro.
         */
        normalizeArray(value) {
            return Array.isArray(value) ? value : [];
        }

        /**
         * Convierte un valor numérico potencialmente inválido en un número seguro.
         */
        normalizeNumber(value, fallback = 0) {
            const number = Number(value);

            return Number.isFinite(number) ? number : fallback;
        }

        /**
         * Normaliza la sección de productos de una respuesta de búsqueda de Nosto.
         */
        normalizeProducts(products = {}) {
            const safeProducts =
                products && typeof products === "object" ? products : {};

            const hits = this.normalizeArray(safeProducts.hits);
            const facets = this.normalizeArray(safeProducts.facets);

            const total =
                safeProducts.total ??
                safeProducts.totalHits ??
                safeProducts.totalCount ??
                hits.length;

            return {
                ...safeProducts,
                hits,
                facets,
                total: this.normalizeNumber(total, hits.length),
                from: this.normalizeNumber(safeProducts.from, 0),
                size: this.normalizeNumber(safeProducts.size, hits.length),
            };
        }

        /**
         * Normaliza la sección de keywords de una respuesta de búsqueda de Nosto.
         */
        normalizeKeywords(keywords = {}) {
            const safeKeywords =
                keywords && typeof keywords === "object" ? keywords : {};

            return {
                ...safeKeywords,
                hits: this.normalizeArray(safeKeywords.hits),
            };
        }

        /**
         * Normaliza la respuesta completa de una búsqueda sin eliminar los datos originales.
         */
        normalizeSearchResponse(response = {}) {
            const safeResponse =
                response && typeof response === "object" ? response : {};

            return {
                ...safeResponse,
                query:
                    typeof safeResponse.query === "string"
                        ? safeResponse.query
                        : "",
                products: this.normalizeProducts(safeResponse.products),
                keywords: this.normalizeKeywords(safeResponse.keywords),
                raw: safeResponse,
            };
        }

        /**
         * Valida que la petición de búsqueda tenga una estructura utilizable.
         */
        validateSearchRequest(request) {
            if (!request || typeof request !== "object") {
                throw new TypeError(
                    "La petición de búsqueda de Nosto debe ser un objeto."
                );
            }

            if (
                !request.products &&
                !request.keywords &&
                typeof request.query !== "string"
            ) {
                throw new Error(
                    "La petición de Nosto debe incluir products, keywords o query."
                );
            }
        }

        /**
         * Ejecuta una búsqueda de Nosto y devuelve una respuesta normalizada.
         */
        async search(request, options = {}) {
            this.validateSearchRequest(request);

            const safeOptions =
                options && typeof options === "object" ? options : {};

            this.log("Ejecutando búsqueda", {
                request,
                options: safeOptions,
            });

            try {
                const response = await this.execute((api) => {
                    if (typeof api.search !== "function") {
                        throw new Error(
                            "La instancia de Nosto no contiene el método api.search."
                        );
                    }

                    return api.search(request, safeOptions);
                });

                const normalizedResponse =
                    this.normalizeSearchResponse(response);

                this.log("Búsqueda completada", normalizedResponse);

                return normalizedResponse;
            } catch (error) {
                this.warn("Error al ejecutar la búsqueda.", error);

                throw error;
            }
        }

        /**
         * Registra que el usuario envió una búsqueda.
         */
        async recordSearchSubmit(query) {
            const normalizedQuery = String(query ?? "").trim();

            if (!normalizedQuery) return false;

            try {
                await this.execute((api) => {
                    if (typeof api.recordSearchSubmit !== "function") {
                        throw new Error(
                            "La instancia de Nosto no contiene recordSearchSubmit."
                        );
                    }

                    return api.recordSearchSubmit(normalizedQuery);
                });

                this.log("Search submit registrado", normalizedQuery);

                return true;
            } catch (error) {
                this.warn("No fue posible registrar el search submit.", error);

                return false;
            }
        }

        /**
         * Registra el clic de un producto mostrado por PLP, SERP o autocomplete.
         */
        async recordSearchClick(track, hit) {
            const normalizedTrack = String(track ?? "").trim();

            if (!normalizedTrack || !hit) {
                this.warn(
                    "No se registró el clic porque faltan track o hit.",
                    {
                        track: normalizedTrack,
                        hit,
                    }
                );

                return false;
            }

            try {
                await this.execute((api) => {
                    if (typeof api.recordSearchClick !== "function") {
                        throw new Error(
                            "La instancia de Nosto no contiene recordSearchClick."
                        );
                    }

                    return api.recordSearchClick(normalizedTrack, hit);
                });

                this.log("Search click registrado", {
                    track: normalizedTrack,
                    hit,
                });

                return true;
            } catch (error) {
                this.warn("No fue posible registrar el search click.", error);

                return false;
            }
        }

        /**
 * Registra que un producto procedente de una búsqueda
 * fue agregado al carrito.
 */
        async recordSearchAddToCart(
            track,
            hit
        ) {
            const normalizedTrack =
                String(
                    track ?? ""
                ).trim();

            if (
                !normalizedTrack ||
                !hit
            ) {
                this.warn(
                    "No se registró el add to cart porque faltan track o hit.",
                    {
                        track:
                            normalizedTrack,

                        hit,
                    }
                );

                return false;
            }

            try {
                await this.execute((api) => {
                    if (
                        typeof api
                            .recordSearchAddToCart !==
                        "function"
                    ) {
                        throw new Error(
                            "La instancia de Nosto no contiene recordSearchAddToCart."
                        );
                    }

                    /*
                     * Conservamos el hit completo devuelto por Nosto.
                     *
                     * Search utiliza metadata del resultado para
                     * relacionar correctamente la interacción con
                     * la búsqueda que originó el producto.
                     */
                    return api
                        .recordSearchAddToCart(
                            normalizedTrack,
                            hit
                        );
                });

                this.log(
                    "Search add to cart registrado",
                    {
                        track:
                            normalizedTrack,

                        hit,
                    }
                );

                return true;
            } catch (error) {
                this.warn(
                    "No fue posible registrar el search add to cart.",
                    error
                );

                return false;
            }
        }

        /**
         * Registra la atribución de un producto recomendado por Nosto.
         */
        async recordAttribution({
            productId,
            attributionKey,
            type = "cp",
            refType = "api",
        } = {}) {
            const normalizedProductId =
                String(productId ?? "").trim();

            const normalizedAttributionKey =
                String(attributionKey ?? "").trim();

            if (!normalizedProductId || !normalizedAttributionKey) {
                this.warn(
                    "No se registró la atribución porque faltan productId o attributionKey."
                );

                return false;
            }

            try {
                await this.execute((api) => {
                    if (typeof api.recordAttribution !== "function") {
                        throw new Error(
                            "La instancia de Nosto no contiene recordAttribution."
                        );
                    }

                    const operation = api.recordAttribution({
                        type: String(type),
                        target: normalizedProductId,
                        ref: normalizedAttributionKey,
                        refType: String(refType),
                    });

                    if (operation && typeof operation.done === "function") {
                        return operation.done();
                    }

                    return operation;
                });

                this.log("Atribución registrada", {
                    productId: normalizedProductId,
                    attributionKey: normalizedAttributionKey,
                });

                return true;
            } catch (error) {
                this.warn(
                    "No fue posible registrar la atribución.",
                    error
                );

                return false;
            }
        }

        /**
         * Actualiza el carrito de la sesión de Nosto usando una lista ya normalizada.
         */
        async updateCart(items = []) {
            const normalizedItems = this.normalizeArray(items);

            try {
                await this.execute((api) => {
                    if (typeof api.defaultSession !== "function") {
                        throw new Error(
                            "La instancia de Nosto no contiene defaultSession."
                        );
                    }

                    const session = api.defaultSession();

                    if (!session || typeof session.setCart !== "function") {
                        throw new Error(
                            "La sesión de Nosto no permite actualizar el carrito."
                        );
                    }

                    const operation = session
                        .setCart({
                            items: normalizedItems,
                        })
                        .viewCart()
                        .update();

                    return operation;
                });

                this.log("Carrito de Nosto actualizado", normalizedItems);

                return true;
            } catch (error) {
                this.warn(
                    "No fue posible actualizar el carrito de Nosto.",
                    error
                );

                return false;
            }
        }
    }

    global.NostoClient = NostoClient;
})(window);